import { BaseStore } from "stores/base_store"
import { runInAction, makeAutoObservable } from "mobx"
import { api_get } from "api/utils"
import { api_post } from "api/utils"
import { CalendarListItemType } from "stores/calendar_auth_store"

export type HasCalendarType = {
    timezone: string;
    calendar_preferences: string;
};

export type CalendarEventType = {
    key?: string;
    id: string;
    summary: string;
    description: string;
    associated_type?: string;
    associated_uuid?: string;
    location?: string;
    colorId?: string;
    visibility?: string;
    transparency?: string;
    editable?: boolean;
    calendar_list_entry?: {
        backgroundColor: string;
        foregroundColor: string;
        colorId: string;
        access_role?: string;
    },
    calendar?: {
        id: string;
    },
    start?: {
        date?: string; // if it's an all day eventa
        dateTime?: string; // if it's a timed event
        timeZone?: string; // if it's a timed event
    };
    end?: {
        date?: string;
        dateTime?: string;
        timeZone?: string;
    };
    timeZone?: string;
    color?: string;
    backgroundColor?: string;
    recurrence?: string[];
    updateProps?: (attributes: CalendarEventType) => void;
};



export class CalendarEvent implements CalendarEventType {
    id = "";
    key = "";
    summary = "";
    description = "";
    location = "";
    colorId = "";
    visibility = "";
    transparency = "";
    editable = false;
    recurrence = [];
    calendar_list_entry = {
        backgroundColor: "",
        foregroundColor: "",
        colorId: ""
    };
    start = {
        date: "",
        dateTime: "",
        timeZone: ""
    };
    end = {
        date: "",
        dateTime: "",
        timeZone: ""
    };

    constructor(attributes: CalendarEventType) {
        makeAutoObservable(this, {
            updateProps: false
        })
        if (attributes) {
            this.updateProps(attributes);
        }
    }

    updateProps(attributes: CalendarEventType) {
        runInAction(() => {
            Object.assign(this, attributes);
        });
    }
}


/* we need to have a key sometimes */
    function copy_id_to_key(event: CalendarEventType) {
        event.key = event.id;
        return event;
    }

export class CalendarEventStore extends BaseStore<CalendarEventType, CalendarEvent> {

    async fetchList(workKey: string,
        associated_type: string,
        associated_uuid: string,
        start: string,
        end: string):Promise<CalendarEventType[]> {
            const params = {
                identity: workKey,
                associated_type: associated_type || '',
                associated_uuid: associated_uuid || '',
                start,
                end
            };
            console.log("fetchList: %o", params);
        const response = await api_get("/calendar-event/list", params);
        if(response.ok) {
            const events = await response.json();
            const converted = events.map((m: CalendarEventType) => copy_id_to_key(m))
            this.receiveUpdate(converted);
            return converted;
        }
        return [] as CalendarEventType[];
    };

    async createEvent(
        workKey: string,
        associated_type: string,
        associated_uuid: string,
        event: CalendarEventType
    ): Promise<CalendarEventType|null> {

        // console.log("createByAssistant: %o", event);
        const data = {
            identity: workKey,
            associated_type: associated_type || '',
            associated_uuid: associated_uuid || '',
            summary: event.summary,
            description: event.description,
            calendar_id: event.calendar?.id || '',
            location: event.location || '',
            start: event.start?.dateTime || '',
            end: event.end?.dateTime || ''
        };

        const response = await api_post("/calendar-event/create", data);
        if(response.ok) {
            const data = await response.json();
            const e = copy_id_to_key(data);
            this.receiveUpdate(copy_id_to_key(e), true);
            return data as CalendarEventType;
        }
        return null;
    };


    async updateEvent(
        workKey: string,
        associated_type: string,
        associated_uuid: string,
        event: CalendarEventType
    ): Promise<CalendarEventType|null> {

        const data = {
            identity: workKey,
            associated_type: associated_type || '',
            associated_uuid: associated_uuid || '',
            id: event.id,
            summary: event.summary,
            description: event.description,
            location: event.location || '',
            start: event.start?.dateTime || '',
            end: event.end?.dateTime || ''
        };

        const response = await api_get("/calendar-event/update", data);
        if(response.ok) {
            const data = await response.json();
            const e = copy_id_to_key(data);
            this.receiveUpdate(copy_id_to_key(e), true);
            return data as CalendarEventType;
        }
        return null;
    };

    async deleteEvent(
        workKey: string,
        associated_type: string,
        associated_uuid: string,
        event_id: string,
        calendar_id: string
    ): Promise<string|null> {

        const data = {
            identity: workKey,
            associated_type: associated_type || '',
            associated_uuid: associated_uuid || '',
            event_id,
            calendar_id
        };

        const response = await api_post("/calendar-event/delete", data);
        if(response.ok) {
            const done = await response.json();
            runInAction(() => {
                this.items = this.items.filter((e) => e.id !== event_id);
            });
            return done;
        }
        return null;
    };

    async fetchSyncedCalendars(
        workKey: string,
        associated_type: string,
        associated_uuid: string,
    ): Promise<CalendarListItemType[]> {
        const params = {
            identity: workKey,
            associated_type: associated_type || '',
            associated_uuid: associated_uuid || '',
        };
        const url = "/calendar-event/google-synced-calendars";
        const response = await api_get(url, params);
        if(response.ok) {
            const data = await response.json();
            const combined = Object.keys(data.calendar_list_items).map((m: string) => {
                const cl = data.calendar_list_items[m];
                const c = data.calendars[m];
                return {
                    ...cl,
                    ...c
                };
            });
            return combined;
        }
        return [] as CalendarListItemType[];
    };


    async fetchHasCalendar(
        workKey: string,
        associated_type: string,
        associated_uuid: string,
    ): Promise<HasCalendarType|null> {
        const params = {
            identity: workKey,
            associated_type: associated_type || '',
            associated_uuid: associated_uuid || '',
        };
        const url = "/calendar-event/has-calendar";
        const response = await api_get(url, params);
        if(response.ok) {
            const data = await response.json();
            if(data.has_calendar) {
                return {
                    timezone: data.timezone_city,
                    calendar_preferences: data.calendar_preferences
                };
            }
        }

        throw "Error fetching calendar data";
    }


    modelConstructor() {
        return CalendarEvent;
    }

}
